<template>
  <div>
    <Layout ref="layoutRef" :selectedOption="2" />
    <h4>
      Tarifario por Prestador
      <span v-if="nivelPrestador" class="badge bg-secondary ms-2 fs-6">
        Nivel {{ ["", "I", "II", "III"][nivelPrestador] }}
      </span>
    </h4>
    <hr />

    <!-- Filtros generales -->
    <div class="container-fluid mb-4">
      <div class="row g-3 align-items-end">
        <div class="col-md-3">
          <label class="form-label fw-bold">Prestador</label>
          <select v-model="filtroPrestador" class="form-select" @change="onCambioPrestador">
            <option value="">-- Seleccione un prestador --</option>
            <option v-for="p in prestadores" :key="p.idPrestador" :value="p.idPrestador">
              {{ p.razonSocial }}
            </option>
          </select>
        </div>
        <div class="col-md-3">
          <label class="form-label fw-bold">Filtrar por Tarifario</label>
          <select v-model="filtroTarifario" class="form-select">
            <option value="">-- Todos los tarifarios --</option>
            <option v-for="t in tarifariosUnicos" :key="t" :value="t">{{ t }}</option>
          </select>
        </div>
        <div class="col-md-2">
          <label class="form-label fw-bold">Estado</label>
          <select v-model="filtroEstado" class="form-select">
            <option value="">-- Todos --</option>
            <option value="1">Activo</option>
            <option value="0">Inactivo</option>
          </select>
        </div>
        <div class="col-md-2">
          <label class="form-label fw-bold">Cruce con tarifario</label>
          <select v-model="filtroCruce" class="form-select">
            <option value="">-- Todos --</option>
            <option value="con">Con tarifario</option>
            <option value="sin">Sin tarifario</option>
          </select>
        </div>
        <div class="col-md-2">
          <label class="form-label fw-bold">Dif. vs tarifario</label>
          <select
            v-model="filtroDif"
            class="form-select"
            :disabled="!nivelPrestador"
            :title="nivelPrestador ? '' : 'Requiere que el prestador tenga nivel 1, 2 o 3'"
          >
            <option value="">-- Todas --</option>
            <option value="mayor">Mayor a 0</option>
            <option value="menor">Menor a 0</option>
          </select>
        </div>
      </div>

      <div class="d-flex justify-content-end align-items-center gap-1 mt-3">
        <span v-if="exportando" class="small text-muted me-2">Preparando archivo...</span>
        <button
          @click="mostrarComparar = true"
          class="btn btn-outline-dark me-2"
          :disabled="!filtroPrestador"
          title="Comparar los valores del prestador con un archivo CSV"
        >
          <i class="material-icons" style="font-size:18px;vertical-align:middle">compare_arrows</i>
          Comparar con archivo
        </button>
        <button
          @click="limpiarTodosLosFiltros"
          class="btn btn-outline-secondary"
          :disabled="!hayFiltros"
          title="Limpiar filtros"
        >
          <i class="material-icons" style="font-size:18px;vertical-align:middle">filter_alt_off</i>
        </button>
        <button
          @click="exportarCSV"
          class="btn btn-outline-success"
          :disabled="totalRegistros === 0 || exportando"
          title="Exportar CSV"
        >
          <i class="material-icons" style="font-size:18px;vertical-align:middle">download</i>
        </button>
        <button
          @click="exportarExcel"
          class="btn btn-outline-primary"
          :disabled="totalRegistros === 0 || exportando"
          title="Exportar Excel"
        >
          <i class="material-icons" style="font-size:18px;vertical-align:middle">grid_on</i>
        </button>
        <button
          @click="exportarPDF"
          class="btn btn-outline-danger"
          :disabled="totalRegistros === 0 || exportando"
          title="Exportar PDF"
        >
          <i class="material-icons" style="font-size:18px;vertical-align:middle">picture_as_pdf</i>
        </button>
      </div>
    </div>

    <!-- Sin prestador seleccionado -->
    <div v-if="!filtroPrestador" class="alert alert-info mt-3" role="alert">
      Seleccione un prestador para ver sus tarifas.
    </div>

    <!-- Primera carga del prestador -->
    <div v-else-if="cargandoInicial" class="text-center my-4">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Cargando...</span>
      </div>
    </div>

    <template v-else>
      <!-- Selector registros por página -->
      <div
        class="d-flex justify-content-end align-items-center gap-2 mb-2"
        v-if="totalRegistros > 0"
      >
        <label class="form-label mb-0 small fw-bold">Mostrar</label>
        <select v-model.number="elementosPorPagina" class="form-select form-select-sm" style="width:75px">
          <option :value="10">10</option>
          <option :value="25">25</option>
          <option :value="50">50</option>
          <option :value="100">100</option>
        </select>
        <span class="small text-muted">registros</span>
      </div>

      <!-- Tabla (se mantiene visible aunque los filtros no devuelvan filas) -->
      <div class="table-responsive" v-if="mostrarTabla">
        <table class="table table-hover table-bordered table-sm tabla-tarifas">
          <thead class="table-dark">
            <tr>
              <th class="text-center">#</th>
              <th
                v-for="col in columnas"
                :key="col.campo"
                class="th-sort"
                :class="[claseAlineacion(col), claseEncabezado(col)]"
                @click="ordenarPor(col.campo)"
              >
                {{ col.titulo }} <span>{{ iconOrden(col.campo) }}</span>
              </th>
            </tr>
            <tr class="fila-filtros">
              <th></th>
              <th v-for="col in columnas" :key="'f-' + col.campo">
                <select
                  v-if="col.tipo === 'estado'"
                  v-model="filtrosColumna[col.campo]"
                  class="form-select form-select-sm"
                >
                  <option value="">Todos</option>
                  <option value="1">Activo</option>
                  <option value="0">Inactivo</option>
                </select>
                <select
                  v-else-if="col.tipo === 'bool'"
                  v-model="filtrosColumna[col.campo]"
                  class="form-select form-select-sm"
                >
                  <option value="">Todos</option>
                  <option value="1">Sí</option>
                  <option value="0">No</option>
                </select>
                <input
                  v-else
                  v-model.trim="filtrosColumna[col.campo]"
                  type="text"
                  class="form-control form-control-sm"
                  :class="{ 'is-invalid': filtroColumnaInvalido(col) }"
                  :placeholder="col.tipo === 'texto' ? 'Filtrar...' : '>0, 5..10'"
                  :title="col.tipo === 'texto' ? '' : 'Ejemplos: >5  <=10  5..10  =0  vacio'"
                />
              </th>
            </tr>
          </thead>
          <tbody :class="{ 'opacity-50': consultando }">
            <tr v-if="totalRegistros === 0 && !consultando">
              <td :colspan="columnas.length + 1" class="text-center text-muted py-3">
                No se encontraron registros con los filtros aplicados.
              </td>
            </tr>
            <tr
              v-for="(item, idx) in lista"
              :key="item.idTarifaPres"
              :title="item.elementoNombre"
              @click="abrirDetalle(item)"
            >
              <td class="text-center">
                {{ (paginaActual - 1) * elementosPorPagina + idx + 1 }}
              </td>
              <td
                v-for="col in columnas"
                :key="col.campo"
                :class="[claseAlineacion(col), claseCelda(col, item)]"
              >
                <template v-if="col.tipo === 'estado'">
                  <span class="badge" :class="item.estado === 1 ? 'bg-success' : 'bg-danger'">
                    {{ item.estado === 1 ? "Activo" : "Inactivo" }}
                  </span>
                </template>
                <template v-else-if="col.tipo === 'moneda'">
                  {{ formatoMoneda(item[col.campo]) }}
                </template>
                <template v-else-if="col.tipo === 'dif'">
                  {{ formatoDif(item[col.campo]) }}
                </template>
                <template v-else-if="col.tipo === 'numero'">
                  {{ formatoNumero(item[col.campo]) }}
                </template>
                <template v-else-if="col.tipo === 'pct'">
                  {{ formatoPct(item[col.campo]) }}
                </template>
                <template v-else-if="col.tipo === 'bool'">
                  {{ item[col.campo] == null ? "—" : item[col.campo] ? "Sí" : "No" }}
                </template>
                <template v-else>
                  {{ item[col.campo] ?? "—" }}
                </template>
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr class="table-secondary">
              <td :colspan="columnas.length + 1" class="text-end fw-bold small">
                Mostrando {{ lista.length }} de {{ totalRegistros }} registros
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div v-if="!mostrarTabla" class="alert alert-warning mt-3" role="alert">
        Este prestador no tiene tarifas registradas.
      </div>

      <!-- Paginación -->
      <div
        class="d-flex justify-content-between align-items-center mt-3"
        v-if="totalRegistros > 0"
      >
        <span class="small text-muted">
          Página {{ paginaActual }} de {{ totalPaginas() }} — {{ totalRegistros }} registros
        </span>
        <nav aria-label="Paginación">
          <ul class="pagination pagination-sm mb-0">
            <li class="page-item" :class="{ disabled: paginaActual === 1 }">
              <a class="page-link" href="#" @click.prevent="aplicarPaginacion(1)">«</a>
            </li>
            <li class="page-item" :class="{ disabled: paginaActual === 1 }">
              <a class="page-link" href="#" @click.prevent="getPreviousPage">‹</a>
            </li>
            <li
              v-for="pagina in paginasVisibles"
              :key="pagina"
              class="page-item"
              :class="{ active: pagina === paginaActual, disabled: pagina === '...' }"
            >
              <a
                class="page-link"
                href="#"
                @click.prevent="pagina !== '...' && aplicarPaginacion(pagina)"
              >
                {{ pagina }}
              </a>
            </li>
            <li class="page-item" :class="{ disabled: paginaActual === totalPaginas() }">
              <a class="page-link" href="#" @click.prevent="getNextPage">›</a>
            </li>
            <li class="page-item" :class="{ disabled: paginaActual === totalPaginas() }">
              <a class="page-link" href="#" @click.prevent="aplicarPaginacion(totalPaginas())">»</a>
            </li>
          </ul>
        </nav>
      </div>
    </template>

    <!-- Popup con el detalle del tarifario -->
    <DetalleTarifarioModal
      v-if="codigoDetalle"
      :codigo="codigoDetalle"
      :nivel="nivelPrestador"
      @cerrar="codigoDetalle = null"
    />

    <!-- Popup para comparar con un archivo CSV -->
    <CompararArchivoModal
      v-if="mostrarComparar"
      :id-prestador="filtroPrestador"
      :prestador="razonSocialPrestador"
      @cerrar="mostrarComparar = false"
    />

    <Footer />
  </div>
</template>

<script>
import api from "@/services/api";
import Layout from "@/components/Layout.vue";
import Footer from "@/components/Footer.vue";
import DetalleTarifarioModal from "@/components/DetalleTarifarioModal.vue";
import CompararArchivoModal from "@/components/CompararArchivoModal.vue";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

// Tamaño de página para exportar (el backend acepta hasta 1000)
const TAM_EXPORTAR = 1000;
// Espera tras dejar de escribir antes de consultar al servidor
const ESPERA_FILTROS_MS = 400;

export default {
  components: { Layout, Footer, DetalleTarifarioModal, CompararArchivoModal },
  data() {
    return {
      // Datos: solo la página actual
      lista:              [],
      totalRegistros:     0,
      totalPaginasApi:    1,
      prestadores:        [],
      tarifariosUnicos:   [],
      // Filtros
      filtroPrestador:    "",
      filtroTarifario:    "",
      filtroEstado:       "",
      filtroCruce:        "",
      filtroDif:          "",      // "" | "mayor" | "menor"
      filtrosColumna:     {},
      codigoDetalle:      null,    // código abierto en el popup
      mostrarComparar:    false,   // popup "Comparar con archivo"
      idEmpresa:          0,
      // Estado de carga
      cargandoInicial:    false,
      consultando:        false,
      exportando:         false,
      reiniciando:        false,
      temporizador:       null,
      secuencia:          0,
      // Paginación y orden (se aplican en el servidor)
      elementosPorPagina: 10,
      paginaActual:       1,
      campoOrden:         "elementoNombre",
      ascendente:         true,
    };
  },
  computed: {
    // Nivel del prestador seleccionado (1, 2 o 3). null si no se conoce.
    nivelPrestador() {
      const p = this.prestadores.find(
        (x) => String(x.idPrestador) === String(this.filtroPrestador)
      );
      const n = p ? Number(p.nivel) : null;
      return [1, 2, 3].includes(n) ? n : null;
    },
    razonSocialPrestador() {
      const p = this.prestadores.find(
        (x) => String(x.idPrestador) === String(this.filtroPrestador)
      );
      return p ? p.razonSocial : "";
    },
    // tipo: texto | moneda | numero | dif | pct | bool | estado
    columnas() {
      const SUFIJO  = { 1: "I", 2: "Ii", 3: "Iii" };
      const ROMANO  = { 1: "I", 2: "II", 3: "III" };
      const niveles = this.nivelPrestador ? [this.nivelPrestador] : [1, 2, 3];
      const unico   = niveles.length === 1;

      const porNivel = [];
      niveles.forEach((n) => {
        const s = SUFIJO[n];
        const r = unico ? "" : ` ${ROMANO[n]}`;
        porNivel.push(
          { campo: `tpsnsUvr${s}`,        titulo: `UVR${r}`,             tipo: "numero", ancho: 10 },
          { campo: `tpsnsFcm${s}`,        titulo: `FCM${r}`,             tipo: "numero", ancho: 10 },
          { campo: `tpsnsValor${s}`,      titulo: `Valor tarifario${r}`, tipo: "moneda", ancho: 14, resaltar: true },
          { campo: `tpsnsUvrAnes${s}`,    titulo: `UVR anes${r}`,        tipo: "numero", ancho: 10 },
          { campo: `tpsnsFcmAnes${s}`,    titulo: `FCM anes${r}`,        tipo: "numero", ancho: 10 },
          { campo: `tpsnsValorAnes${s}`,  titulo: `Valor anes${r}`,      tipo: "moneda", ancho: 12, resaltar: true },
          { campo: `difValorVsNivel${s}`, titulo: `Dif. vs Nivel ${ROMANO[n]}`, tipo: "dif", ancho: 14 },
          { campo: `pctVsNivel${s}`,      titulo: `% vs Nivel ${ROMANO[n]}`,    tipo: "pct", ancho: 12 }
        );
        // Reglas TPSNS: solo aplican a establecimientos de nivel I y II
        if (n < 3) {
          porNivel.push(
            { campo: `reglaAplicada${s}`,       titulo: `Regla aplicada${r}`,    tipo: "texto",  ancho: 16 },
            { campo: `uvrAplicable${s}`,        titulo: `UVR aplicable${r}`,     tipo: "numero", ancho: 12 },
            { campo: `valorAplicable${s}`,      titulo: `Valor aplicable${r}`,   tipo: "moneda", ancho: 14, resaltar: "aplicable" },
            { campo: `uvrAnesAplicable${s}`,    titulo: `UVR anes aplic.${r}`,   tipo: "numero", ancho: 12 },
            { campo: `valorAnesAplicable${s}`,  titulo: `Valor anes aplic.${r}`, tipo: "moneda", ancho: 14, resaltar: "aplicable" }
          );
        }
      });

      return [
        { campo: "elementoCodigo",           titulo: "Código",         tipo: "texto",  ancho: 12 },
        // { campo: "elementoNombre",        titulo: "Elemento",       tipo: "texto",  ancho: 60 },
        // { campo: "tarifaConvNombre",      titulo: "Tarifario",      tipo: "texto",  ancho: 30 },
        { campo: "valor",                    titulo: "Valor prestador", tipo: "moneda", ancho: 14, resaltar: true },
        { campo: "tpsnsTipoProcedimiento",   titulo: "Tipo",           tipo: "texto",  ancho: 8  },
        ...porNivel,
        { campo: "tpsnsIncrementoCosta",     titulo: "Inc. Costa",     tipo: "bool",   ancho: 10 },
        { campo: "tpsnsIncrementoGalapagos", titulo: "Inc. Galápagos", tipo: "bool",   ancho: 14 },
        { campo: "estado",                   titulo: "Estado",         tipo: "estado", ancho: 10 },
      ];
    },
    hayFiltros() {
      return (
        this.filtroTarifario !== "" || this.filtroEstado !== "" ||
        this.filtroCruce !== "" || this.filtroDif !== "" ||
        Object.values(this.filtrosColumna).some((v) => v !== "" && v != null)
      );
    },
    // La tabla (con su fila de filtros) se mantiene si hay filtros, aunque no haya resultados
    mostrarTabla() {
      return this.totalRegistros > 0 || this.hayFiltros || this.consultando;
    },
    paginasVisibles() {
      const total  = this.totalPaginas();
      const actual = this.paginaActual;
      const delta  = 2;
      const rango  = [];
      for (let i = Math.max(2, actual - delta); i <= Math.min(total - 1, actual + delta); i++) {
        rango.push(i);
      }
      if (rango.length && rango[0] > 2)                        rango.unshift("...");
      if (rango.length && rango[rango.length - 1] < total - 1) rango.push("...");
      const paginas = [1];
      rango.forEach((p) => paginas.push(p));
      if (total > 1) paginas.push(total);
      return paginas;
    },
  },
  watch: {
    // Si cambian las columnas (otro nivel) se reinician los filtros por columna
    columnas: {
      immediate: true,
      handler() { this.limpiarFiltrosColumna(); },
    },
    // Filtros: esperan a que el usuario deje de escribir y vuelven a la página 1
    filtroTarifario()  { this.filtrosCambiaron(); },
    filtroEstado()     { this.filtrosCambiaron(); },
    filtroCruce()      { this.filtrosCambiaron(); },
    filtroDif()        { this.filtrosCambiaron(); },
    filtrosColumna: {
      deep: true,
      handler() { this.filtrosCambiaron(); },
    },
    elementosPorPagina() { this.aplicarPaginacion(1); },
  },
  mounted() {
    this.idEmpresa = window.sessionStorage.getItem("idEmpresa");
    this.$refs.layoutRef.toggleProcesos();
    this.$refs.layoutRef.toggleReportes();
    this.$refs.layoutRef.toggleParametros();
    this.$refs.layoutRef.toggleAyuda();
    this.cargarPrestadores();
  },
  beforeUnmount() {
    clearTimeout(this.temporizador);
  },
  methods: {
    // ── Formato ────────────────────────────────────────────
    formatoMoneda(valor) {
      if (valor === null || valor === undefined) return "—";
      return Number(valor).toLocaleString("es-EC", { style: "currency", currency: "USD" });
    },
    formatoDif(valor) {
      if (valor === null || valor === undefined) return "—";
      const n = Number(valor);
      const txt = Math.abs(n).toLocaleString("es-EC", { style: "currency", currency: "USD" });
      return n > 0 ? `+${txt}` : n < 0 ? `-${txt}` : txt;
    },
    formatoNumero(valor) {
      if (valor === null || valor === undefined) return "—";
      return Number(valor).toLocaleString("es-EC", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 4,
      });
    },
    formatoPct(valor) {
      if (valor === null || valor === undefined) return "—";
      const n = Number(valor);
      const txt = n.toLocaleString("es-EC", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      return `${n > 0 ? "+" : ""}${txt}%`;
    },
    claseAlineacion(col) {
      if (["moneda", "dif", "numero", "pct"].includes(col.tipo)) return "text-end";
      if (col.tipo === "estado" || col.tipo === "bool") return "text-center";
      return "";
    },
    // Verde: valor prestador por encima del tarifario. Rojo: por debajo.
    claseEncabezado(col) {
      if (!col.resaltar) return "";
      return col.resaltar === "aplicable" ? "th-aplicable" : "th-resaltado";
    },
    claseCelda(col, item) {
      if (col.resaltar) {
        return col.resaltar === "aplicable"
          ? "fw-bold celda-aplicable"
          : "fw-bold celda-resaltada";
      }
      if (col.tipo !== "dif" && col.tipo !== "pct") return "";
      const v = item[col.campo];
      if (v === null || v === undefined) return "text-muted";
      if (Number(v) > 0) return "text-success fw-semibold";
      if (Number(v) < 0) return "text-danger fw-semibold";
      return "";
    },
    // ── Popup de detalle ───────────────────────────────────
    abrirDetalle(item) {
      const cod = (item.elementoCodigo ?? "").trim();
      if (cod) this.codigoDetalle = cod;
    },
    // ── Orden ──────────────────────────────────────────────
    ordenarPor(campo) {
      if (this.campoOrden === campo) {
        this.ascendente = !this.ascendente;
      } else {
        this.campoOrden = campo;
        this.ascendente = true;
      }
      this.aplicarPaginacion(1);
    },
    iconOrden(campo) {
      if (this.campoOrden !== campo) return "⇅";
      return this.ascendente ? "↑" : "↓";
    },
    // ── Paginación (cada cambio consulta al servidor) ──────
    totalPaginas() {
      return Math.max(1, this.totalPaginasApi);
    },
    aplicarPaginacion(noPagina) {
      this.paginaActual = noPagina;
      this.consultar();
    },
    getPreviousPage() {
      if (this.paginaActual > 1) this.aplicarPaginacion(this.paginaActual - 1);
    },
    getNextPage() {
      if (this.paginaActual < this.totalPaginas()) this.aplicarPaginacion(this.paginaActual + 1);
    },
    // ── Filtros ────────────────────────────────────────────
    filtrosCambiaron() {
      if (this.reiniciando || !this.filtroPrestador) return;
      clearTimeout(this.temporizador);
      this.temporizador = setTimeout(() => this.aplicarPaginacion(1), ESPERA_FILTROS_MS);
    },
    limpiarFiltrosColumna() {
      const f = {};
      this.columnas.forEach((c) => { f[c.campo] = ""; });
      this.filtrosColumna = f;
    },
    limpiarTodosLosFiltros() {
      this.filtroTarifario = "";
      this.filtroEstado    = "";
      this.filtroCruce     = "";
      this.filtroDif       = "";
      this.limpiarFiltrosColumna();
    },
    // Valida expresiones numéricas: ">5", "<=10", "5..10", "=0", "8,13" o "vacio"
    // (la interpretación real se hace en el servidor)
    expresionNumericaValida(expr) {
      const e = expr.replace(/\s+/g, "").replace(/,/g, ".").toLowerCase();
      const N = "-?\\d+(?:\\.\\d+)?";
      return (
        e === "vacio" ||
        new RegExp(`^${N}\\.\\.${N}$`).test(e) ||
        new RegExp(`^(>=|<=|>|<|=)${N}$`).test(e) ||
        new RegExp(`^${N}$`).test(e)
      );
    },
    filtroColumnaInvalido(col) {
      if (["texto", "estado", "bool"].includes(col.tipo)) return false;
      const f = (this.filtrosColumna[col.campo] ?? "").toString().trim();
      return f !== "" && !this.expresionNumericaValida(f);
    },
    // ── Consultas al servidor ──────────────────────────────
    construirParams(page, size) {
      const params = {
        page,
        size,
        sort: this.campoOrden,
        dir:  this.ascendente ? "asc" : "desc",
      };
      if (this.filtroTarifario)      params.tarifario = this.filtroTarifario;
      if (this.filtroEstado !== "")  params.estado    = this.filtroEstado;
      if (this.filtroCruce)          params.cruce     = this.filtroCruce;
      this.columnas.forEach((c) => {
        const v = (this.filtrosColumna[c.campo] ?? "").toString().trim();
        if (v !== "" && !this.filtroColumnaInvalido(c)) params[`f.${c.campo}`] = v;
      });
      // Filtro rápido de diferencia: usa la columna "Dif." del nivel del prestador
      // (tiene prioridad sobre lo escrito en el filtro de esa columna)
      if (this.filtroDif) {
        const colDif = this.columnas.find((c) => c.tipo === "dif");
        if (colDif) params[`f.${colDif.campo}`] = this.filtroDif === "mayor" ? ">0" : "<0";
      }
      return params;
    },
    async cargarPrestadores() {
      try {
        const res = await api.get(`/vwprestadors/idempresa/${this.idEmpresa}/servicios`);
        this.prestadores = (Array.isArray(res.data) ? res.data : []).sort((a, b) =>
          (a.razonSocial ?? "").localeCompare(b.razonSocial ?? "", "es")
        );
      } catch (error) {
        console.error("Error cargando prestadores:", error);
      }
    },
    async onCambioPrestador() {
      const id = this.filtroPrestador;
      clearTimeout(this.temporizador);

      // "reiniciando" evita que el cambio de filtros dispare consultas de más
      this.reiniciando = true;
      this.limpiarTodosLosFiltros();
      this.campoOrden       = "elementoNombre";
      this.ascendente       = true;
      this.paginaActual     = 1;
      this.lista            = [];
      this.totalRegistros   = 0;
      this.tarifariosUnicos = [];
      await this.$nextTick();
      this.reiniciando = false;

      if (!id) return;
      this.cargandoInicial = true;
      this.cargarTarifarios();      // en paralelo: alimenta el desplegable
      await this.consultar();
      if (this.filtroPrestador === id) this.cargandoInicial = false;
    },
    async cargarTarifarios() {
      const id = this.filtroPrestador;
      try {
        const res = await api.get(`/tarifa-pres-ext/prestador/${id}/tarifarios`);
        if (this.filtroPrestador === id) {
          this.tarifariosUnicos = Array.isArray(res.data) ? res.data : [];
        }
      } catch (error) {
        console.error("Error cargando tarifarios:", error);
      }
    },
    // Trae SOLO la página actual. Si llega una respuesta vieja, se descarta.
    async consultar() {
      const id = this.filtroPrestador;
      if (!id) return;
      const seq = ++this.secuencia;
      this.consultando = true;
      try {
        const res = await api.get(`/tarifa-pres-ext/prestador/${id}`, {
          params: this.construirParams(this.paginaActual - 1, this.elementosPorPagina),
        });
        if (seq !== this.secuencia) return;

        const data = res.data ?? {};
        this.lista           = data.content ?? [];
        this.totalRegistros  = data.totalElements ?? data.page?.totalElements ?? 0;
        this.totalPaginasApi = data.totalPages ?? data.page?.totalPages ?? 1;

        // Si tras filtrar la página actual ya no existe, vuelve a la última válida
        if (this.totalPaginasApi > 0 && this.paginaActual > this.totalPaginasApi) {
          this.paginaActual = this.totalPaginasApi;
          return this.consultar();
        }
      } catch (error) {
        if (seq !== this.secuencia) return;
        console.error("Error cargando las tarifas:", error);
        this.lista = [];
        this.totalRegistros = 0;
      } finally {
        if (seq === this.secuencia) this.consultando = false;
      }
    },
    // Para exportar: trae TODO lo filtrado (páginas de 1000, en paralelo)
    async obtenerTodoFiltrado() {
      const url = `/tarifa-pres-ext/prestador/${this.filtroPrestador}`;
      const primera = await api.get(url, { params: this.construirParams(0, TAM_EXPORTAR) });
      const d = primera.data ?? {};
      const filas = [...(d.content ?? [])];
      const total = d.totalPages ?? d.page?.totalPages ?? 1;
      if (total > 1) {
        const resto = await Promise.all(
          Array.from({ length: total - 1 }, (_, i) =>
            api.get(url, { params: this.construirParams(i + 1, TAM_EXPORTAR) })
          )
        );
        resto.forEach((r) => filas.push(...(r.data?.content ?? [])));
      }
      return filas;
    },
    async filasParaExportar() {
      this.exportando = true;
      try {
        return await this.obtenerTodoFiltrado();
      } catch (error) {
        console.error("Error obteniendo datos para exportar:", error);
        alert("No se pudieron obtener los datos para exportar.");
        return null;
      } finally {
        this.exportando = false;
      }
    },
    // ── Valor de una columna para exportar ─────────────────
    valorExport(item, col, formateado = false) {
      const v = item[col.campo];
      if (col.tipo === "estado") return v === 1 ? "Activo" : "Inactivo";
      if (col.tipo === "bool") return v === null || v === undefined ? "" : v ? "Sí" : "No";
      if (col.tipo === "numero") {
        if (v === null || v === undefined) return formateado ? "" : null;
        return formateado ? this.formatoNumero(v) : Number(v);
      }
      if (col.tipo === "pct") {
        if (v === null || v === undefined) return formateado ? "" : null;
        return formateado ? this.formatoPct(v) : Number(v);
      }
      if (col.tipo === "moneda" || col.tipo === "dif") {
        if (v === null || v === undefined) return formateado ? "" : null;
        return formateado ? this.formatoMoneda(v) : Number(v);
      }
      return v ?? "";
    },
    // ── Exportar CSV (separador ";") ───────────────────────
    async exportarCSV() {
      const datos = await this.filasParaExportar();
      if (!datos) return;
      const escape = (val) => {
        if (val === null || val === undefined) return "";
        const str = String(val).replace(/"/g, '""');
        return str.includes(";") || str.includes("\n") ? `"${str}"` : str;
      };
      const headers = ["#", ...this.columnas.map((c) => c.titulo)];
      const rows = [
        headers.join(";"),
        ...datos.map((item, idx) =>
          [idx + 1, ...this.columnas.map((c) => this.valorExport(item, c))]
            .map(escape)
            .join(";")
        ),
      ];
      const bom  = "\uFEFF";
      const blob = new Blob([bom + rows.join("\n")], { type: "text/csv;charset=utf-8;" });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      a.download = "reporte_tarifas_prestador.csv";
      a.click();
      URL.revokeObjectURL(url);
    },
    // ── Exportar Excel (.xlsx) ──────────────────────────────
    async exportarExcel() {
      const datos = await this.filasParaExportar();
      if (!datos) return;
      const filas = datos.map((item, idx) => {
        const fila = { "#": idx + 1 };
        this.columnas.forEach((c) => {
          fila[c.titulo] = this.valorExport(item, c);
        });
        return fila;
      });

      const hoja = XLSX.utils.json_to_sheet(filas);
      hoja["!cols"] = [{ wch: 5 }, ...this.columnas.map((c) => ({ wch: c.ancho }))];

      const rango = XLSX.utils.decode_range(hoja["!ref"]);
      this.columnas.forEach((c, i) => {
        if (!["moneda", "dif", "pct"].includes(c.tipo)) return;
        for (let fila = rango.s.r + 1; fila <= rango.e.r; fila++) {
          const celda = hoja[XLSX.utils.encode_cell({ r: fila, c: i + 1 })];
          if (celda) celda.z = c.tipo === "pct" ? '0.00"%"' : '"$"#,##0.00';
        }
      });

      const libro = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(libro, hoja, "Tarifas");
      XLSX.writeFile(libro, "reporte_tarifas_prestador.xlsx");
    },
    // ── Exportar PDF (horizontal) ──────────────────────────
    async exportarPDF() {
      const datos = await this.filasParaExportar();
      if (!datos) return;
      const doc = new jsPDF({ orientation: "landscape" });
      doc.setFontSize(14);
      doc.text("Reporte de Tarifas por Prestador", 14, 15);
      autoTable(doc, {
        startY: 20,
        head: [["#", ...this.columnas.map((c) => c.titulo)]],
        body: datos.map((item, idx) => [
          idx + 1,
          ...this.columnas.map((c) => this.valorExport(item, c, true)),
        ]),
        styles:     { fontSize: 7 },
        headStyles: { fillColor: [33, 37, 41] },
      });
      doc.save("reporte_tarifas_prestador.pdf");
    },
  },
};
</script>

<style scoped>
.th-sort {
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}
.th-sort:hover {
  background-color: #3d5166;
}
/* Columnas de valor (Valor prestador, Valor tarifario, Valor anes.) */
.celda-resaltada {
  background-color: #fff3cd;
}
.th-resaltado {
  background-color: #7a5b00;
}
/* Columnas de valor aplicable (reglas TPSNS) */
.celda-aplicable {
  background-color: #d9ecff;
}
.th-aplicable {
  background-color: #0d5a9c;
}
.table-hover tbody tr {
  cursor: pointer;
}
.tabla-tarifas {
  font-size: 0.85rem;
}
.fila-filtros th {
  padding: 2px 4px;
  font-weight: normal;
  min-width: 90px;
}
.fila-filtros .form-control,
.fila-filtros .form-select {
  font-size: 0.75rem;
}
</style>